import {
  DataPlatformAudienceRule,
  DataPlatformLoginStatus,
  DataPlatformQueryResponse,
  DataPlatformVisitEvent,
} from './data-platform.model';
import { Audience, AudienceFilter, AudienceFilterType } from '../audience/components/audience.model';
import { frequencyOf, matchedValues, timeframeOf } from './audiences/comparable-rules.utils';
import { TranslateService } from '@ngx-translate/core';
import { InterestProfile } from '../account-settings/models/interest-profiles.model';
import {
  clusterDimensionValues,
  dimensionComparisonKey,
  DimensionCluster,
  formatDimensionValue,
  splitDimensionLongTail,
  stripProportions,
} from './dimension-display.utils';

/** A rendered identity-source badge for the profile / contact flyouts. */
export interface IdentitySourceBadge {
  /** i18n key for the label. */
  labelKey: string;
  /** Material icon name, for non-provider sources. Mutually exclusive with logoUrl. */
  icon?: string;
  /** Brand logo path, for ESP-provider sources. Mutually exclusive with icon. */
  logoUrl?: string;
  /** When true, only render for admins (adminToggle). */
  adminOnly: boolean;
  /** i18n key of the recorded onsite trigger, if any. */
  triggerKey?: string;
  /** i18n key explaining an Audience analysis card row; email platforms take `{ provider }`. */
  descriptionKey?: string;
}

const ONSITE_LABEL_KEY = 'data-platform.identity-source.onsite';
const MANUAL_IMPORT_LABEL_KEY = 'data-platform.identity-source.manual_import';
const MIGRATION_LABEL_KEY = 'data-platform.identity-source.migration';
const DESCRIPTION_KEY_PREFIX = 'data-platform.identity-source-description.';

// Onsite triggers and their card icons.
const ONSITE_TRIGGER_ICONS = new Map<string, string>([
  ['tracker_login', 'login'],
  ['tracker_purchase', 'shopping_cart'],
  ['tracker_onsite_campaign', 'campaign'],
  ['tracker_membership', 'card_membership'],
  ['tracker_site_form', 'edit_note'],
  ['tracker_other', 'more_horiz'],
]);

// Onsite sources without a recorded trigger.
const ONSITE_UNKNOWN_TRIGGER_SOURCES = new Set(['tracker_reported', 'tracker_remembered', 'onsite', 'event']);

// ESP provider (id_map source) -> brand logo under /assets/img/logos.
const IDENTITY_SOURCE_LOGOS: Record<string, string> = {
  voyado: 'voyado.png',
  rule: 'rule-logo.png',
  mailchimp: 'mailchimp_freddie_color_web.png',
  apsis: 'apsis.png',
  klaviyo: 'klaviyo.svg',
  emarsys: 'emarsys.png',
  custobar: 'custobar.png',
  dotdigital: 'dotdigital.png',
  omnisend: 'omnisend.png',
  brevo: 'brevo.jpg',
};

/**
 * Maps a `source` (id_map / contact_identifiers) to a flyout badge, or null when there is nothing
 * meaningful to show. Onsite values carry their trigger in `triggerKey`. Aliases: `onsite`/`event` ->
 * tracker, `import`/`repair` -> migration. ESP providers render their brand logo; the rest use a
 * Material icon. `migration` is admin-only.
 */
export function identitySourceBadge(source: string | null | undefined): IdentitySourceBadge | null {
  const token = (source ?? '').trim().toLowerCase();
  if (!token) {
    return null;
  }
  if (ONSITE_TRIGGER_ICONS.has(token)) {
    return {
      labelKey: ONSITE_LABEL_KEY,
      icon: 'public',
      adminOnly: false,
      triggerKey: 'data-platform.identity-source.' + token,
    };
  }
  if (ONSITE_UNKNOWN_TRIGGER_SOURCES.has(token)) {
    return { labelKey: ONSITE_LABEL_KEY, icon: 'public', adminOnly: false };
  }
  if (token === 'manual_import') {
    return { labelKey: MANUAL_IMPORT_LABEL_KEY, icon: 'upload_file', adminOnly: false };
  }
  if (token === 'migration' || token === 'import' || token === 'repair') {
    return { labelKey: MIGRATION_LABEL_KEY, icon: 'font_download', adminOnly: true };
  }
  const logoFile = IDENTITY_SOURCE_LOGOS[token];
  if (logoFile) {
    return {
      labelKey: 'data-platform.identity-source.' + token,
      logoUrl: '/assets/img/logos/' + logoFile,
      adminOnly: false,
    };
  }
  return { labelKey: 'data-platform.identity-source.integration', icon: 'mail', adminOnly: false };
}

/** Card row for an identity_source_detail value: its trigger, Unknown for other onsite values, else the badge. */
export function identitySourceDetail(source: string | null | undefined): IdentitySourceBadge | null {
  const token = (source ?? '').trim().toLowerCase();
  const triggerIcon = ONSITE_TRIGGER_ICONS.get(token);
  if (triggerIcon) {
    return {
      labelKey: 'data-platform.identity-source.' + token,
      icon: triggerIcon,
      adminOnly: false,
      descriptionKey: DESCRIPTION_KEY_PREFIX + token,
    };
  }
  if (ONSITE_UNKNOWN_TRIGGER_SOURCES.has(token)) {
    return {
      labelKey: 'data-platform.identity-source.unknown',
      icon: 'help_outline',
      adminOnly: false,
      descriptionKey: DESCRIPTION_KEY_PREFIX + 'unknown',
    };
  }
  const badge = identitySourceBadge(token);
  if (!badge || badge.labelKey === MIGRATION_LABEL_KEY) {
    return badge;
  }
  const kind = badge.labelKey === MANUAL_IMPORT_LABEL_KEY ? 'manual_import' : 'integration';
  return { ...badge, descriptionKey: DESCRIPTION_KEY_PREFIX + kind };
}

/** The filter bar fills only goals (any time, at least once) and tags, and ORs a facet's values, so one included list of each. */
export function isAudienceCompatible(audience: Audience, canExcludeTags: boolean): boolean {
  if (!audience.filters?.length || audience.sessionConditions?.length) {
    return false;
  }
  const included = new Set<AudienceFilterType>();
  for (const filter of audience.filters) {
    const facet = filterBarFacet(filter, canExcludeTags);
    if (!facet) {
      return false;
    }
    if (facet.included) {
      if (included.has(facet.type)) {
        return false;
      }
      included.add(facet.type);
    }
  }
  return true;
}

function filterBarFacet(
  filter: AudienceFilter,
  canExcludeTags: boolean,
): { type: AudienceFilterType; included: boolean } | null {
  const values = matchedValues(filter);
  if (!values) {
    return null;
  }
  if (filter.type === AudienceFilterType.Tags) {
    return values.negated && !canExcludeTags ? null : { type: filter.type, included: !values.negated };
  }
  if (filter.type !== AudienceFilterType.Goals) {
    return null;
  }
  const timeframe = timeframeOf(filter);
  const frequency = frequencyOf(filter);
  const anyTime = timeframe !== null && Object.keys(timeframe).length === 0;
  // A "not" goal means never, whatever its frequency.
  const atLeastOnce = values.negated || (frequency?.operator === 'gte' && frequency.value === 1);
  return anyTime && atLeastOnce ? { type: filter.type, included: !values.negated } : null;
}

export function isLoggedIn(loginStatus: DataPlatformLoginStatus | undefined | null): boolean {
  return loginStatus === 'LoggedIn';
}

export function loginStatusLabelKey(loginStatus: DataPlatformLoginStatus | undefined | null): string | null {
  switch (loginStatus) {
    case 'LoggedIn':
      return 'data-platform.logged-in';
    case 'LoggedOut':
      return 'data-platform.logged-out';
    default:
      return null;
  }
}

export function getVisitPageViewCount(events: DataPlatformVisitEvent[]): number {
  return events?.filter((e) => e.eventType === 'pageview').length ?? 0;
}

export function getVisitGoalCount(events: DataPlatformVisitEvent[]): number {
  return events?.filter((e) => e.goalName).length ?? 0;
}

export function collectVisitGoalNames(events: DataPlatformVisitEvent[]): string[] {
  if (!events) return [];
  const names = new Set<string>();
  events.forEach((e) => {
    if (e.goalName) names.add(e.goalName);
  });
  return Array.from(names);
}

export function collectVisitDimensions(events: DataPlatformVisitEvent[]): string[] {
  const dims = new Set<string>();
  events?.forEach((e) => e.dimensions?.forEach((d) => dims.add(d)));
  return Array.from(dims);
}

/** English fallback per dimension type; the translated labels live under data-platform.dimensionTypes. */
export const DIMENSION_TYPE_LABELS: Record<string, string> = {
  pageType: 'Page type',
  category: 'Category',
  tags: 'Tags',
  brand: 'Brand',
  gender: 'Gender',
  color: 'Color',
  size: 'Size',
  material: 'Material',
  author: 'Author',
  url: 'URL',
};

/** Callers with a TranslateService get the translated name; the English map is the fallback. */
export function getDimensionTypeLabel(type: string, translate?: TranslateService): string {
  const key = 'data-platform.dimensionTypes.' + type;
  const translated = translate?.instant(key);
  if (translated && translated !== key) {
    return translated;
  }
  return DIMENSION_TYPE_LABELS[type] ?? type;
}

export interface DimensionAffinity {
  dimension: string;
  count: number;
}

export function collectVisitDimensionAffinity(events: DataPlatformVisitEvent[]): DimensionAffinity[] {
  const counts = new Map<string, number>();
  events?.forEach((e) =>
    e.dimensions?.forEach((d) => {
      counts.set(d, (counts.get(d) ?? 0) + 1);
    }),
  );
  return Array.from(counts.entries())
    .map(([dimension, count]) => ({ dimension, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Ranks a pre-sorted composites array by position, for chips that only need an order.
 * The counts are positions, not measurements, so never show one as a quantity.
 */
export function collectVisitDimensionAffinityFromComposites(composites: string[]): DimensionAffinity[] {
  return composites.filter((d) => d).map((dimension, i) => ({ dimension, count: composites.length - i }));
}

export interface DimensionTypeGroup {
  type: string;
  affinities: DimensionAffinity[];
}

export function groupVisitDimensionsByType(events: DataPlatformVisitEvent[]): DimensionTypeGroup[] {
  const typeMap = new Map<string, Map<string, number>>();
  events?.forEach((e) =>
    e.dimensions?.forEach((d) => {
      const colonIdx = d.indexOf(':');
      if (colonIdx === -1) return;
      const type = d.substring(0, colonIdx);
      const value = d.substring(colonIdx + 1);
      if (!typeMap.has(type)) typeMap.set(type, new Map());
      const valueMap = typeMap.get(type)!;
      valueMap.set(value, (valueMap.get(value) ?? 0) + 1);
    }),
  );
  return Array.from(typeMap.entries()).map(([type, valueMap]) => ({
    type,
    affinities: Array.from(valueMap.entries())
      .map(([dimension, count]) => ({ dimension, count }))
      .sort((a, b) => b.count - a.count),
  }));
}

export function collectVisitTags(events: DataPlatformVisitEvent[]): string[] {
  if (!events) return [];
  const tags = new Set<string>();
  events.forEach((e) => e.tags?.forEach((t) => tags.add(t)));
  return Array.from(tags);
}

/** Coupon state from the visit's events; the per-code list only exists once the session is finalized. */
export function collectCouponActivity(events: DataPlatformVisitEvent[]): { shown: boolean; copied: boolean } {
  if (!events) return { shown: false, copied: false };
  return {
    shown: events.some((e) => e.eventType === 'event' && e.actionTitle === 'coupon-distributed'),
    copied: events.some(
      (e) => e.eventType === 'widget-copy-coupon' || (e.eventType === 'event' && e.actionTitle === 'copy-coupon'),
    ),
  };
}

export function getStarIcon(score: number): string {
  if (score >= 67) return 'star';
  if (score >= 34) return 'star_half';
  return 'star_border';
}

export type EngagementTier = 'hot' | 'active' | 'warming' | 'cold';

/**
 * Single source of truth for engagement tiers: how an engagement score (0–100)
 * maps to an intent tier, the tier's Material icon, and its score band.
 * Thresholds mirror the server's EngagementScorer.GetTier(). Both the badge
 * (`getTier`) and the list filter (`tiersToScoreBands`) derive from this table,
 * so the bands are defined exactly once. Ordered high → low.
 */
export const ENGAGEMENT_TIERS: ReadonlyArray<{ key: EngagementTier; min: number; max: number; icon: string }> = [
  { key: 'hot', min: 71, max: 100, icon: 'local_fire_department' },
  { key: 'active', min: 41, max: 70, icon: 'trending_up' },
  { key: 'warming', min: 21, max: 40, icon: 'visibility' },
  { key: 'cold', min: 0, max: 20, icon: 'ac_unit' },
];

/** Returns the intent tier label matching EngagementScorer.GetTier() on the server. */
export function getTier(score: number): EngagementTier {
  const band = ENGAGEMENT_TIERS.find((tier) => score >= tier.min);
  return (band ?? ENGAGEMENT_TIERS[ENGAGEMENT_TIERS.length - 1]).key;
}

/** Material icon name for each intent tier. */
export function getTierIcon(tier: EngagementTier): string {
  return ENGAGEMENT_TIERS.find((t) => t.key === tier)?.icon ?? 'ac_unit';
}

/**
 * Maps the selected tiers to inclusive engagement-score bands — one per selected tier,
 * with adjacent/contiguous tiers merged into a single band. A profile/visit matches if its
 * score falls in ANY band, so non-contiguous selections (e.g. Hot + Cold) exclude the middle
 * tiers instead of spanning across them. Returns an empty array when nothing is selected.
 */
export function tiersToScoreBands(tiers: readonly EngagementTier[] | undefined | null): { min: number; max: number }[] {
  const selected = ENGAGEMENT_TIERS.filter((tier) => tiers?.includes(tier.key));
  if (!selected.length) return [];
  const sorted = [...selected].sort((a, b) => a.min - b.min);
  const bands: { min: number; max: number }[] = [];
  for (const tier of sorted) {
    const last = bands[bands.length - 1];
    if (last && tier.min <= last.max + 1) {
      last.max = Math.max(last.max, tier.max); // contiguous — merge
    } else {
      bands.push({ min: tier.min, max: tier.max });
    }
  }
  return bands;
}

export function getEngagementStars(score: number): string[] {
  return Array.from({ length: 5 }, (_, i) => {
    if (score >= (i + 1) * 20) return 'star';
    if (score >= i * 20 + 10) return 'star_half';
    return 'star_border';
  });
}

/**
 * Mapping from URL pattern (interest profile path) to human-readable name.
 * Used to translate URL dimension values (e.g., "url:/products/shoes") to their configured names.
 */
export type UrlInterestProfileMap = Map<string, string>;

/**
 * Extracts the URL pattern from a dimension composite string.
 * For "url:/products/shoes" returns "/products/shoes".
 * For "category:Electronics" returns undefined (not a URL dimension).
 */
export function extractUrlPatternFromDimension(dimension: string): string | undefined {
  const colonIdx = dimension.indexOf(':');
  if (colonIdx === -1) return undefined;
  const type = dimension.substring(0, colonIdx);
  if (type !== 'url') return undefined;
  return dimension.substring(colonIdx + 1);
}

/**
 * Translates a dimension composite string using URL interest profile names.
 * If the dimension is of type "url" and has a matching interest profile name,
 * returns "Url: <name>" instead of "url:<url-pattern>".
 * Otherwise returns the original formatted label.
 */
export function translateDimensionLabel(
  dimension: string,
  urlInterestProfileMap: UrlInterestProfileMap,
  translate?: TranslateService,
): string {
  const urlPattern = extractUrlPatternFromDimension(dimension);
  if (urlPattern && urlInterestProfileMap.has(urlPattern)) {
    return `${getDimensionTypeLabel('url', translate)}: ${urlInterestProfileMap.get(urlPattern)}`;
  }
  // Default formatting for non-URL dimensions or URL patterns without a name
  const colonIdx = dimension.indexOf(':');
  if (colonIdx === -1) {
    return getDimensionTypeLabel(dimension, translate);
  }
  const type = dimension.substring(0, colonIdx);
  const value = dimension.substring(colonIdx + 1);
  return `${getDimensionTypeLabel(type, translate)}: ${formatDimensionValue(value, type)}`;
}

/**
 * Translates a type:value composite string using URL interest profile names.
 * Same as translateDimensionLabel but works with pre-split type/value pairs.
 */
export function translateDimensionTypeValue(
  type: string,
  value: string,
  urlInterestProfileMap: UrlInterestProfileMap,
  translate?: TranslateService,
): string {
  if (type === 'url' && urlInterestProfileMap.has(value)) {
    return `${getDimensionTypeLabel('url', translate)}: ${urlInterestProfileMap.get(value)}`;
  }
  return `${getDimensionTypeLabel(type, translate)}: ${formatDimensionValue(value, type)}`;
}

export const DONUT_COLORS = [
  'rgba(90,172,208,1)',
  'rgba(242,142,237,1)',
  'rgba(255,177,21,1)',
  'rgba(33,226,232,1)',
  'rgba(120,200,120,1)',
  'rgba(200,120,120,1)',
  'rgba(180,160,220,1)',
];

/** Colour of the row that stands for the long tail. Deliberately outside the palette. */
const OTHER_COLOR = 'rgba(176,180,190,1)';
const MAX_TOOLTIP_MEMBERS = 8;

export interface DimensionDonutSegment {
  dimension: string;
  count: number;
  percentage: number;
  dashArray: string;
  dashOffset: string;
  color: string;
  /** Set when the row stands for more than its label: folded spellings, or the tail. */
  membersTooltip?: string;
}

export interface DimensionTypeDonut {
  type: string;
  displayType: string;
  /** The values worth their own colour, with the long tail as a single "Other" row. */
  segments: DimensionDonutSegment[];
  /** Every value, for when the reader expands the group. Empty when nothing is collapsed. */
  allSegments: DimensionDonutSegment[];
  /** How many values "Other" stands for. 0 when the group already shows everything. */
  hiddenCount: number;
  /** Owned by the template: whether the reader expanded this group. */
  showAll?: boolean;
}

/**
 * Builds one type's donut: near-duplicates folded, the long tail behind "Other", and the
 * full list for the expanded view. Both views take percentages from the whole type.
 */
export function buildDimensionTypeDonut(
  type: string,
  affinities: DimensionAffinity[],
  map: UrlInterestProfileMap,
  translate?: TranslateService,
): DimensionTypeDonut {
  const clusters = clusterDimensionValues(
    affinities.map((affinity) => {
      // The legend sits under a per-type header (BRAND, URL, ...), so drop the type prefix.
      const colonIdx = affinity.dimension.indexOf(':');
      const value = colonIdx > -1 ? affinity.dimension.substring(colonIdx + 1) : '';
      return { value: displayDimensionValue(type, value, map), count: affinity.count };
    }),
  );
  const total = clusters.reduce((sum, cluster) => sum + cluster.count, 0);
  const { shown, hidden } = splitDimensionLongTail(clusters);
  const collapsed = hidden.length > 0 ? [...shown, buildOtherCluster(hidden, translate)] : shown;
  return {
    type,
    displayType: getDimensionTypeLabel(type, translate),
    segments: buildSegments(collapsed, total, hidden.length > 0 ? collapsed.length - 1 : -1, translate),
    allSegments: hidden.length > 0 ? buildSegments(clusters, total, -1, translate) : [],
    hiddenCount: hidden.length,
  };
}

/** URL patterns read as their interest-profile name; everything else is re-cased for display. */
export function displayDimensionValue(type: string, value: string, map: UrlInterestProfileMap): string {
  if (type === 'url') {
    return map.get(value) ?? value;
  }
  // Only a material carries a proportion; elsewhere a number is part of the value.
  return formatDimensionValue(type === 'material' ? stripProportions(value) : value, type);
}

function buildOtherCluster(hidden: DimensionCluster[], translate?: TranslateService): DimensionCluster {
  return {
    value: translateOrFallback('dimension-other', 'Other', translate),
    count: hidden.reduce((sum, cluster) => sum + cluster.count, 0),
    members: hidden.map((cluster) => cluster.value),
  };
}

function buildSegments(
  clusters: DimensionCluster[],
  total: number,
  otherIndex: number,
  translate?: TranslateService,
): DimensionDonutSegment[] {
  let cumulative = 0;
  return clusters.map((cluster, i) => {
    const pct = total > 0 ? (cluster.count / total) * 100 : 0;
    // Members differing only in case or accents tell the reader nothing, so they stay quiet.
    const folded =
      i === otherIndex
        ? cluster.members
        : cluster.members
            .slice(1)
            .filter((member) => dimensionComparisonKey(member) !== dimensionComparisonKey(cluster.value));
    const segment: DimensionDonutSegment = {
      dimension: cluster.value,
      count: cluster.count,
      percentage: Math.round(pct),
      dashArray: `${pct} ${100 - pct}`,
      dashOffset: `${25 - cumulative}`,
      color: i === otherIndex ? OTHER_COLOR : DONUT_COLORS[i % DONUT_COLORS.length],
      ...(folded.length > 0 && { membersTooltip: buildMembersTooltip(folded, translate) }),
    };
    cumulative += pct;
    return segment;
  });
}

function buildMembersTooltip(values: string[], translate?: TranslateService): string {
  const head = values.slice(0, MAX_TOOLTIP_MEMBERS);
  const remaining = values.length - head.length;
  const list = remaining > 0 ? `${head.join(', ')} +${remaining}` : head.join(', ');
  return translateOrFallback('dimension-includes', `Includes: ${list}`, translate, { values: list });
}

function translateOrFallback(
  key: string,
  fallback: string,
  translate?: TranslateService,
  params?: Record<string, unknown>,
): string {
  const fullKey = 'data-platform.' + key;
  const translated = translate?.instant(fullKey, params);
  return translated && translated !== fullKey ? translated : fallback;
}

export function buildDimensionTypeDonutFromComposites(
  composites: string[],
  map: UrlInterestProfileMap,
  translate?: TranslateService,
  scores?: Record<string, number>,
): DimensionTypeDonut[] {
  const affinities = scores
    ? composites.filter((dimension) => dimension).map((dimension) => ({ dimension, count: scores[dimension] ?? 0 }))
    : collectVisitDimensionAffinityFromComposites(composites);
  return buildDimensionTypeDonutFromAffinities(affinities, map, translate);
}

/**
 * Builds per-type donuts for a profile spanning many visits: each dimension is counted
 * once per visit it appears in (real occurrence counts), then grouped by type.
 * Do NOT flatten the per-visit lists into buildDimensionTypeDonutFromComposites instead —
 * that helper assumes ONE deduplicated pre-ranked list, so a flattened multi-visit list
 * renders one duplicate segment per visit with synthetic position-based counts.
 */
export function buildDimensionTypeDonutFromVisits(
  perVisitComposites: string[][],
  map: UrlInterestProfileMap,
  translate?: TranslateService,
): DimensionTypeDonut[] {
  const visitCounts = new Map<string, number>();
  for (const composites of perVisitComposites) {
    for (const dimension of composites) {
      if (dimension) {
        visitCounts.set(dimension, (visitCounts.get(dimension) ?? 0) + 1);
      }
    }
  }
  const affinities = Array.from(visitCounts.entries())
    .map(([dimension, count]) => ({ dimension, count }))
    .sort((a, b) => b.count - a.count);
  return buildDimensionTypeDonutFromAffinities(affinities, map, translate);
}

function buildDimensionTypeDonutFromAffinities(
  allAffinities: DimensionAffinity[],
  map: UrlInterestProfileMap,
  translate?: TranslateService,
): DimensionTypeDonut[] {
  const typeMap = new Map<string, DimensionAffinity[]>();
  for (const affinity of allAffinities) {
    const colonIdx = affinity.dimension.indexOf(':');
    if (colonIdx === -1) {
      continue;
    }
    const type = affinity.dimension.substring(0, colonIdx);
    if (!typeMap.has(type)) {
      typeMap.set(type, []);
    }
    typeMap.get(type)!.push(affinity);
  }
  return Array.from(typeMap.entries()).map(([type, affinities]) =>
    buildDimensionTypeDonut(type, affinities, map, translate),
  );
}

// ---------------------------------------------------------------------------
// Flyout insight presets
// ---------------------------------------------------------------------------

export interface InsightPreset {
  id: string;
  label: string;
  icon: string;
  /** Controls the chip color in the flyout: green = good, red = at-risk, blue = neutral. */
  variant: 'positive' | 'warning' | 'neutral';
  rules: DataPlatformAudienceRule[];
}

/**
 * Returns a curated list of audience-insight presets for use in flyouts.
 * Each matched preset becomes a visible chip (e.g. "Risk of Churn").
 * @param purchaseName The account-specific purchase goal name (e.g. "purchase" or "order").
 */
export function getInsightPresets(purchaseName: string): InsightPreset[] {
  return [
    {
      id: 'new-visitor',
      label: 'New Visitor',
      icon: 'fiber_new',
      variant: 'neutral',
      rules: [{ type: 'first_seen', daysAgo: 7 }],
    },
    {
      id: 'active-buyer',
      label: 'Active Buyer',
      icon: 'loyalty',
      variant: 'positive',
      rules: [
        { type: 'engagement_score', operator: 'gte', value: 70 },
        { type: 'revenue', operator: 'gt', value: 0 },
      ],
    },
    {
      id: 'high-spenders',
      label: 'High Spender',
      icon: 'attach_money',
      variant: 'positive',
      rules: [{ type: 'revenue', operator: 'gt', value: 10000 }],
    },
    {
      id: 'repeat-purchaser',
      label: 'Repeat Purchaser',
      icon: 'repeat',
      variant: 'positive',
      rules: [{ type: 'purchase_count', operator: 'gte', value: 3 }],
    },
    {
      id: 'risk-of-churn',
      label: 'Risk of Churn',
      icon: 'warning',
      variant: 'warning',
      rules: [
        { type: 'revenue', operator: 'gt', value: 0 },
        { type: 'engagement_score', operator: 'lte', value: 35 },
      ],
    },
    {
      id: 'lapsed-buyer',
      label: 'Lapsed Buyer',
      icon: 'history',
      variant: 'warning',
      rules: [
        { type: 'revenue', operator: 'gt', value: 0 },
        { type: 'last_active', operator: 'gt', daysAgo: 60 },
      ],
    },
    {
      id: 'high-intent-no-purchase',
      label: 'High Intent',
      icon: 'explore',
      variant: 'neutral',
      rules: [
        { type: 'engagement_score', operator: 'gte', value: 60 },
        { type: 'revenue', operator: 'eq', value: 0 },
      ],
    },
    {
      id: 'abandoned-cart',
      label: 'Abandoned Cart',
      icon: 'shopping_cart',
      variant: 'warning',
      rules: [
        { type: 'event_count', eventName: 'add_to_cart', operator: 'gte', value: 1 },
        { type: 'purchase_count', operator: 'eq', value: 0 },
      ],
    },
  ];
}

/**
 * Creates a UrlInterestProfileMap from an array of InterestProfile objects.
 * Maps from each profile's paths (URL patterns) to its name. Also maps from id to name.
 */
export function createUrlInterestProfileMap(profiles: InterestProfile[]): UrlInterestProfileMap {
  const map = new Map<string, string>();
  for (const profile of profiles) {
    if (profile.name) {
      // Map from id to name
      if (profile.id) {
        map.set(profile.id, profile.name);
      }
      // Map from each path to name
      if (profile.paths?.length) {
        for (const path of profile.paths) {
          map.set(path, profile.name);
        }
      }
    }
  }
  return map;
}

/**
 * Above this many rows an export is worth warning about first. Rows rather than bytes: the server
 * work is per row, and the size shown to the user follows from it.
 */
export const EXPORT_WARN_ROWS = 100_000;

/** Rough average CSV row size per entity, measured against real exported rows. */
export const EXPORT_BYTES_PER_VISIT = 300;
export const EXPORT_BYTES_PER_PROFILE = 140;

/** Rounded MB, for the pre-export warning and the progress estimate. */
export function estimatedExportSize(rows: number, bytesPerRow: number): string {
  return `${Math.round((rows * bytesPerRow) / (1024 * 1024))} MB`;
}

/** Name of the single non-time facet column in a /query response, or `fallback` when it has none. */
export function facetColumn(response: DataPlatformQueryResponse | null | undefined, fallback = 'label'): string {
  return response?.columns?.find((column) => column.role === 'facet')?.name ?? fallback;
}
